/**
 * Axios client — single instance for the whole app.
 *
 * Adds:
 *  - `Authorization: Bearer <access>` from auth store
 *  - 401 → silent refresh-token retry; only logout if refresh also fails
 *    (PULSE-203 — 30-min access tokens used to bounce people to /login)
 */
import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

export const api = axios.create({
  baseURL: '/api',
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Request: inject bearer + account scope (PULSE-47) ─────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const state = useAuthStore.getState()
  if (state.accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${state.accessToken}`
  }
  // Auto-inject `account=<sub_id|all>` only for masters and only on
  // data endpoints. Skip auth/* — the master-detection flow doesn't
  // know its scope yet at that point.
  const url = config.url ?? ''
  const isAuthRoute = url.startsWith('/auth') || url.startsWith('auth')
  if (state.user?.is_master && !isAuthRoute) {
    config.params = {
      account: state.selectedAccount,
      ...(config.params ?? {}),
    }
  }
  return config
})

// ─── Response: 401 → silent refresh + retry, logout only if that fails ──

function logout() {
  useAuthStore.getState().clear()
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// One in-flight refresh shared by all concurrent 401s — they await it and
// retry with the new access token (no thundering herd of refresh calls).
let refreshing: Promise<string | null> | null = null

async function doRefresh(): Promise<string | null> {
  const rt = useAuthStore.getState().refreshToken
  if (!rt) return null
  try {
    // Bare axios (not `api`) — avoids interceptor recursion + bearer inject.
    const { data } = await axios.post('/api/auth/refresh', { refresh_token: rt })
    useAuthStore.getState().setTokens(
      { access: data.access_token, refresh: data.refresh_token },
      data.user,
    )
    return data.access_token as string
  } catch {
    return null
  }
}

function isAuthCall(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/register')
  )
}

api.interceptors.response.use(
  (resp) => resp,
  async (err: AxiosError) => {
    const original = err.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined
    const status = err.response?.status
    const url = original?.url ?? ''

    if (status !== 401 || !original || isAuthCall(url)) {
      return Promise.reject(err)
    }
    // Already retried once, or no refresh token → give up → logout.
    if (original._retried || !useAuthStore.getState().refreshToken) {
      if (useAuthStore.getState().accessToken) logout()
      return Promise.reject(err)
    }

    original._retried = true
    if (!refreshing) {
      refreshing = doRefresh().finally(() => {
        refreshing = null
      })
    }
    const newAccess = await refreshing
    if (!newAccess) {
      logout()
      return Promise.reject(err)
    }
    original.headers = original.headers ?? {}
    original.headers.Authorization = `Bearer ${newAccess}`
    return api(original as AxiosRequestConfig)
  },
)
